/**
 * Source-linked procedural geometry briefs for the next cool-season models.
 *
 * These are rendering directions, not a claim that any specimen has passed
 * botanical or turfgrass-expert review. Relative dimensions are intentionally
 * normalized so a renderer can keep diagnostic structures legible without
 * implying cultivar-level measurements or exact scale.
 */

export type CoolSeasonAtlasSlug =
  | 'tall-fescue'
  | 'fine-fescue'
  | 'perennial-ryegrass'
  | 'annual-ryegrass'

export type EvidenceStrength = 'direct-extension-description' | 'source-conflict' | 'group-level-only'
export type VernationShape = 'folded-v' | 'rolled-tube'
export type BladeTipShape = 'pointed-flat' | 'keeled-boat'
export type SurfaceFinish = 'dull' | 'glossy-underneath' | 'ridged-upper-dull-underneath'
export type AuricleTreatment = 'absent' | 'rudimentary' | 'long-clasping' | 'withhold-until-review'
export type GrowthForm = 'bunch' | 'bunch-with-optional-short-rhizomes' | 'group-variant'
export type SeedheadForm = 'open-panicle' | 'spike'

export interface ModelSource {
  id: string
  publisher: string
  title: string
  href: string
}

export interface EvidenceNote {
  trait: string
  sourceIds: readonly string[]
  strength: EvidenceStrength
  note: string
}

/**
 * Values map descriptive words such as "very narrow" and "broad" onto a
 * 0–1 procedural scale. They are art-direction values, never field measures.
 */
export interface ProceduralBladeGeometry {
  relativeWidth: number
  relativeStiffness: number
  posture: 'upright' | 'upright-slight-arch' | 'tufted-fountain'
  tip: BladeTipShape
  vernation: VernationShape
  surface: SurfaceFinish
  veinTreatment: 'many-equal-ridges' | 'prominent-midrib-plus-fine-veins' | 'veins-subdued'
  crossSection: 'flat' | 'folded-v'
}

export interface ProceduralCollarGeometry {
  ligule: 'short-membranous'
  auricles: AuricleTreatment
  sheath: 'open-overlapping-smooth' | 'open-overlapping-ribbed'
}

export interface ProceduralGrowthGeometry {
  form: GrowthForm
  tillerDensity: 'open' | 'medium' | 'dense'
  stolons: 'none'
  rhizomes: 'none' | 'optional-short-subsoil' | 'group-dependent'
  roots: 'fibrous' | 'deep-fibrous'
  seedhead: SeedheadForm
  /** Only show awns if a model-specific reviewed source is added. */
  awns: 'not-modeled-without-reviewed-source'
}

export interface CoolSeasonProceduralModelSpec {
  slug: CoolSeasonAtlasSlug
  displayName: string
  scientificScope: string
  modelStatus: 'source-linked-procedural-brief-expert-review-pending'
  reviewBoundary: string
  sourceIds: readonly string[]
  blade: ProceduralBladeGeometry
  collar: ProceduralCollarGeometry
  growth: ProceduralGrowthGeometry
  evidence: readonly EvidenceNote[]
  uncertainties: readonly string[]
  integration: {
    existingAtlasSlug: CoolSeasonAtlasSlug
    requiredHotspots: readonly ['blade-tip', 'blade', 'collar', 'node', 'runner', 'roots', 'seedhead']
    /** Keep diagnostic components intentionally enlarged, with an in-product disclosure. */
    diagnosticGeometryScale: 'enlarged-for-legibility-not-to-scale'
  }
}

export const COOL_SEASON_MODEL_SOURCES = {
  pennStateCoolSeasonIdentification: {
    id: 'psu-cool-season-id',
    publisher: 'Penn State Extension',
    title: 'The Cool-season Turfgrasses: Identification',
    href: 'https://extension.psu.edu/the-cool-season-turfgrasses-identification',
  },
  umnPastureGrassIdentification: {
    id: 'umn-pasture-grass-id',
    publisher: 'University of Minnesota Extension',
    title: 'Identifying common cool-season perennial pasture grasses',
    href: 'https://extension.umn.edu/forage-variety-selection/identifying-common-cool-season-perennial-pasture-grasses',
  },
  umnFineFescue: {
    id: 'umn-fine-fescue',
    publisher: 'University of Minnesota Extension',
    title: 'Planting and maintaining a fine fescue lawn',
    href: 'https://extension.umn.edu/lawns-and-landscapes-minnesota/planting-and-maintaining-fine-fescue-lawn',
  },
  umnTallFescue: {
    id: 'umn-tall-fescue',
    publisher: 'University of Minnesota Extension',
    title: 'Tall Fescue',
    href: 'https://apps.extension.umn.edu/garden/diagnose/weed/grass/tallfescue.html',
  },
} as const satisfies Record<string, ModelSource>

export const COOL_SEASON_PROCEDURAL_MODEL_SPECS: Record<
  CoolSeasonAtlasSlug,
  CoolSeasonProceduralModelSpec
> = {
  'tall-fescue': {
    slug: 'tall-fescue',
    displayName: 'Tall Fescue',
    scientificScope: 'Schedonorus arundinaceus (syn. Festuca arundinacea); turf-type forms vary by cultivar.',
    modelStatus: 'source-linked-procedural-brief-expert-review-pending',
    reviewBoundary:
      'This is a source-linked geometry brief, not an expert-reviewed model. Do not present the collar as a single-trait confirmation.',
    sourceIds: ['psu-cool-season-id', 'umn-tall-fescue', 'umn-pasture-grass-id'],
    blade: {
      relativeWidth: 0.76,
      relativeStiffness: 0.78,
      posture: 'upright-slight-arch',
      tip: 'pointed-flat',
      vernation: 'rolled-tube',
      surface: 'ridged-upper-dull-underneath',
      veinTreatment: 'many-equal-ridges',
      crossSection: 'flat',
    },
    collar: {
      ligule: 'short-membranous',
      // Penn State reports no auricles while a University of Minnesota pasture
      // reference reports short, sometimes hairy auricles. Withhold the shape.
      auricles: 'withhold-until-review',
      sheath: 'open-overlapping-ribbed',
    },
    growth: {
      form: 'bunch-with-optional-short-rhizomes',
      tillerDensity: 'medium',
      stolons: 'none',
      rhizomes: 'optional-short-subsoil',
      roots: 'deep-fibrous',
      seedhead: 'open-panicle',
      awns: 'not-modeled-without-reviewed-source',
    },
    evidence: [
      {
        trait: 'Rolled vernation; many equal-sized lengthwise veins; dull lower blade; pointed tip.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Use a broad, flat blade with repeated upper-surface ridges and no single dominant midrib.',
      },
      {
        trait: 'Flat, stiff blade; prominent upper veins; coarse clump; fibrous roots.',
        sourceIds: ['umn-tall-fescue'],
        strength: 'direct-extension-description',
        note: 'Bias the teaching pose toward an upright, stiff clump and deepen the fibrous root fan.',
      },
      {
        trait: 'Short membranous ligule; panicle seedhead; primarily bunch type.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Render no stolons. Keep optional short rhizomes below the soil as a subdued, review-gated variant.',
      },
      {
        trait: 'Auricle appearance.',
        sourceIds: ['psu-cool-season-id', 'umn-pasture-grass-id'],
        strength: 'source-conflict',
        note: 'Penn State says absent; the Minnesota pasture reference says short and sometimes hairy. Do not render a definitive auricle until reviewed against turf-type tall fescue references.',
      },
    ],
    uncertainties: [
      'Turf-type cultivar variation is not represented by this single normalized specimen.',
      'Penn State notes that short rhizomes may occur, while the Minnesota lawn reference describes no rhizomes. Keep rhizomes optional and visually secondary.',
      'The collar auricle conflict must be resolved by a turfgrass expert before the model is promoted.',
    ],
    integration: {
      existingAtlasSlug: 'tall-fescue',
      requiredHotspots: ['blade-tip', 'blade', 'collar', 'node', 'runner', 'roots', 'seedhead'],
      diagnosticGeometryScale: 'enlarged-for-legibility-not-to-scale',
    },
  },
  'fine-fescue': {
    slug: 'fine-fescue',
    displayName: 'Fine Fescue',
    scientificScope:
      'Fine fescue group, including strong creeping red, slender creeping red, Chewings, hard, and sheep fescue. This is not one species model.',
    modelStatus: 'source-linked-procedural-brief-expert-review-pending',
    reviewBoundary:
      'This group-level teaching specimen must not be labeled as a species-level or cultivar-level visual identification model.',
    sourceIds: ['psu-cool-season-id', 'umn-fine-fescue'],
    blade: {
      relativeWidth: 0.18,
      relativeStiffness: 0.38,
      posture: 'tufted-fountain',
      tip: 'keeled-boat',
      vernation: 'folded-v',
      surface: 'dull',
      veinTreatment: 'veins-subdued',
      crossSection: 'folded-v',
    },
    collar: {
      ligule: 'short-membranous',
      auricles: 'absent',
      sheath: 'open-overlapping-smooth',
    },
    growth: {
      form: 'group-variant',
      tillerDensity: 'dense',
      stolons: 'none',
      rhizomes: 'group-dependent',
      roots: 'fibrous',
      seedhead: 'open-panicle',
      awns: 'not-modeled-without-reviewed-source',
    },
    evidence: [
      {
        trait: 'Folded vernation; very narrow folded blades; veins usually not visible; dull underside; keeled tip.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Use a bristle-fine folded V profile and suppress vein meshes rather than inventing a bold midrib.',
      },
      {
        trait: 'Auricles absent; short membranous ligule; bunch or rhizomatous habit; panicle seedhead.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Use a tuft as the default. Rhizomes belong only to a clearly labeled creeping-red variant.',
      },
      {
        trait: 'Fine fescue is a group of five look-alike turfgrasses with growth-habit differences.',
        sourceIds: ['umn-fine-fescue'],
        strength: 'group-level-only',
        note: 'Render one shared group plate with toggles or inset variants rather than implying a single canonical plant.',
      },
    ],
    uncertainties: [
      'No group-level geometry can distinguish strong creeping red, slender creeping red, Chewings, hard, and sheep fescue reliably.',
      'Creeping red forms can make rhizomes, while other common fine fescues are bunchier. This needs a named-species variant before a reviewed 3D claim.',
    ],
    integration: {
      existingAtlasSlug: 'fine-fescue',
      requiredHotspots: ['blade-tip', 'blade', 'collar', 'node', 'runner', 'roots', 'seedhead'],
      diagnosticGeometryScale: 'enlarged-for-legibility-not-to-scale',
    },
  },
  'perennial-ryegrass': {
    slug: 'perennial-ryegrass',
    displayName: 'Perennial Ryegrass',
    scientificScope: 'Lolium perenne; perennial bunchgrass.',
    modelStatus: 'source-linked-procedural-brief-expert-review-pending',
    reviewBoundary:
      'The model emphasizes the folded shoot, glossy lower leaf surface, and rudimentary auricles.',
    sourceIds: ['psu-cool-season-id', 'umn-pasture-grass-id'],
    blade: {
      relativeWidth: 0.43,
      relativeStiffness: 0.54,
      posture: 'upright-slight-arch',
      tip: 'keeled-boat',
      vernation: 'folded-v',
      surface: 'glossy-underneath',
      veinTreatment: 'prominent-midrib-plus-fine-veins',
      crossSection: 'folded-v',
    },
    collar: {
      ligule: 'short-membranous',
      auricles: 'rudimentary',
      sheath: 'open-overlapping-smooth',
    },
    growth: {
      form: 'bunch',
      tillerDensity: 'medium',
      stolons: 'none',
      rhizomes: 'none',
      roots: 'fibrous',
      seedhead: 'spike',
      awns: 'not-modeled-without-reviewed-source',
    },
    evidence: [
      {
        trait: 'Folded vernation; dominant midvein plus smaller veins; glossy underside; keeled tip.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Use a distinct underside material so rotation reveals gloss; preserve the V-shaped shoot cross-section.',
      },
      {
        trait: 'Small, rudimentary auricles and a short membranous ligule.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Model tiny non-clasping ear-like collar projections, enlarged only enough to inspect.',
      },
      {
        trait: 'Bunch growth with spike seedhead.',
        sourceIds: ['psu-cool-season-id', 'umn-pasture-grass-id'],
        strength: 'direct-extension-description',
        note: 'Use tillers and fibrous roots only. The flowering stem should carry a flattened spike, not a branched panicle.',
      },
    ],
    uncertainties: [
      'Blade sheen varies with light, leaf age, moisture, and camera angle. It is supporting evidence, not a confirmation by itself.',
      'The model does not encode cultivar-level blade width or color variation.',
    ],
    integration: {
      existingAtlasSlug: 'perennial-ryegrass',
      requiredHotspots: ['blade-tip', 'blade', 'collar', 'node', 'runner', 'roots', 'seedhead'],
      diagnosticGeometryScale: 'enlarged-for-legibility-not-to-scale',
    },
  },
  'annual-ryegrass': {
    slug: 'annual-ryegrass',
    displayName: 'Annual Ryegrass',
    scientificScope: 'Lolium multiflorum; annual bunchgrass.',
    modelStatus: 'source-linked-procedural-brief-expert-review-pending',
    reviewBoundary:
      'The model emphasizes the rolled shoot and long clasping auricles that separate annual from perennial ryegrass.',
    sourceIds: ['psu-cool-season-id'],
    blade: {
      relativeWidth: 0.5,
      relativeStiffness: 0.58,
      posture: 'upright-slight-arch',
      tip: 'keeled-boat',
      vernation: 'rolled-tube',
      surface: 'glossy-underneath',
      veinTreatment: 'prominent-midrib-plus-fine-veins',
      crossSection: 'flat',
    },
    collar: {
      ligule: 'short-membranous',
      auricles: 'long-clasping',
      sheath: 'open-overlapping-smooth',
    },
    growth: {
      form: 'bunch',
      tillerDensity: 'open',
      stolons: 'none',
      rhizomes: 'none',
      roots: 'fibrous',
      seedhead: 'spike',
      awns: 'not-modeled-without-reviewed-source',
    },
    evidence: [
      {
        trait: 'Rolled vernation; dominant midvein plus smaller veins; glossy underside; keeled tip.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Make the shoot cross-section round instead of V-shaped. This is the key procedural contrast with perennial ryegrass.',
      },
      {
        trait: 'Long clasping auricles with a short membranous ligule.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Use long, thin collar arms that visibly wrap the shoot, with a separate short ligule membrane behind them.',
      },
      {
        trait: 'Bunch growth and spike-type seedhead.',
        sourceIds: ['psu-cool-season-id'],
        strength: 'direct-extension-description',
        note: 'Do not add stolons or rhizomes. Render a spike only; this source does not establish awn geometry for this model.',
      },
    ],
    uncertainties: [
      'The existing atlas description calls the seedhead awned, but the cited Penn State turfgrass source establishes only a spike type. Do not model awns until a reviewed model-specific source is reconciled.',
      'Gloss on the lower leaf surface is lighting-sensitive and should be paired with vernation and auricle cues.',
    ],
    integration: {
      existingAtlasSlug: 'annual-ryegrass',
      requiredHotspots: ['blade-tip', 'blade', 'collar', 'node', 'runner', 'roots', 'seedhead'],
      diagnosticGeometryScale: 'enlarged-for-legibility-not-to-scale',
    },
  },
}
